/* eslint-disable no-console */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import moment from 'moment';
import { shortenandHoverString, dateFormate, pageSize, imageUrl, formatDateOrDash } from '../../../Components/helper';
import BombiixAGGrid, { ServerSortHeader } from '../../../Components/BombiixAGGrid';
import { NewCheckbox } from '../../../Components/PIM/common/NewCheckbox';
import {
  ArchiveIcon,
  ClearIcon,
  CloseIcon,
  CommentsIcon,
  DeleteIcon,
  DownloadIcon,
  DuplicateIcon,
  EditIcon,
  InfoIcon,
  MoreIcon,
  PreviewIcon,
  UnarchiveIcon,
  UpdateIcon,
} from '../../../bombiix-icon';
import { BombiixStatusPill } from '../../../Components/BombiixStatusPill';
import * as supplierAction from '../store/Supplier.action';
import { toasterMsg } from '../../../Components/Toaster/Toaster';
import CustomTooltip from '../../../Components/Tooltip/CustomTooltip';
import './Document.css';
import { BombiixInfoPopover } from '../../../Components/BombiixInfoPopover';
import { formatStoredCalendarDate, isStoredCalendarDateBeforeToday } from '../../../Components/helper';

/**
 * Flags a document whose expiry date has already passed, comparing the stored calendar day with
 * the viewer's today so the highlight cannot flip a day early or late.
 * @param {*} expiredAt - Stored expiry value.
 * @returns {boolean} True when the expiry date is before today.
 */
function isExpiringOrExpired(expiredAt) {
  return isStoredCalendarDateBeforeToday(expiredAt);
}

/**
 * Document name, with a link through to a template document's own files page.
 * @param {object} params - AG Grid cell params.
 * @returns {React.ReactElement} The name cell.
 */
function DocumentTitleCell({ data }) {
  return (
    <>
      {data?.title ? shortenandHoverString(data.title, 20) : ''}
      {data?.type === 'Template' && (
        <Link className="bombiix-icon-container" to={`/supplier/${data?.supplierId}/document/${data?._id}/files?from=document`}>
          <span className="icon-detail_page" />
        </Link>
      )}
    </>
  );
}

/**
 * Expiry date, reddened once the date has passed.
 * @param {object} params - AG Grid cell params.
 * @returns {React.ReactElement} The expiry cell.
 */
function DocumentExpiryCell({ data }) {
  return (
    <span className={isExpiringOrExpired(data?.expiredAt) ? 'text-danger' : ''}>
      {data?.expiredAt ? formatStoredCalendarDate(data?.expiredAt, 'DD-MMM-YYYY') : null}
    </span>
  );
}

/**
 * Status text with its colour bar, and the approval-comment popover unless this
 * is the archive view.
 * @param {object} params - AG Grid cell params plus `renderComment` / `renderStatusBar`.
 * @returns {React.ReactElement} The status cell.
 */
const STATUS_TONE = {
  green: 'approved',
  red: 'rejected',
  amber: 'pending',
};

/**
 * Maps a document's stored colour — or, when the API sends none, its status
 * text — onto a pill tone. Mirrors the mapping the old colour bar used, so a
 * document keeps the colour it has always had.
 * @param {string} colourStatus - `statusColor` as stored.
 * @param {string} itemStatus - Human-readable status.
 * @returns {string} A `BombiixStatusPill` tone.
 */
function resolveStatusTone(colourStatus, itemStatus) {
  let colour = colourStatus;
  if (!colour && itemStatus) {
    if (itemStatus === 'Approved') colour = 'green';
    else if (itemStatus === 'Rejected' || itemStatus === 'rejected') colour = 'red';
    else colour = 'amber';
  }
  return STATUS_TONE[colour] ?? 'neutral';
}

/**
 * Status cell: the split `Uploaded | <status>` pill, plus the approval-comment
 * popover unless this is the archive view.
 * @param {object} params - AG Grid cell params plus `renderComment` and `leadingLabel`.
 * @returns {React.ReactElement} The status cell.
 */
function DocumentStatusCell({ data, renderComment, leadingLabel }) {
  return (
    <div className="d-flex align-items-center doc-status-content">
      <BombiixStatusPill leadingLabel={leadingLabel} label={data?.status} tone={resolveStatusTone(data?.statusColor || '', data?.status)} />
      {renderComment ? renderComment(data) : null}
    </div>
  );
}

/**
 * Header checkbox selecting or clearing every document on the page.
 * @param {object} params - AG Grid header params plus `checked`, `label`, `onToggle`.
 * @returns {React.ReactElement} The header checkbox.
 */
function DocumentSelectAllHeader({ checked, label, onToggle }) {
  return <NewCheckbox id="check_all" name="check_all" checked={checked} aria-label={label} onChange={onToggle} />;
}

/**
 * Per-row selection checkbox.
 * @param {object} params - AG Grid cell params plus `selectedDocuments`, `label`, `onToggle`.
 * @returns {React.ReactElement} The row checkbox.
 */
function DocumentSelectRowCell({ data, selectedDocuments, label, onToggle }) {
  return (
    <NewCheckbox
      id={`checks_${data?._id}`}
      name={`checks_${data?._id}`}
      checked={(selectedDocuments ?? []).filter((val) => val === data?._id).length > 0}
      aria-label={label}
      onChange={(e) => onToggle(e, data?._id, data)}
    />
  );
}

function DocumentTabel({
  supplierId,
  handleSelectAll,
  supportingDocument,
  selectDocumentCheckBox,
  allSelectedDocuments,
  Documents,
  onDelete,
  onEdit,
  onPreview,
  onRenew,
  onSortColumn,
  loading,
  CostingAddEditAccess,
  CostingDeleteAccess,
  archiveSection,
  handleArchiveCard,
  handleDuplicateById,
  isFactoryLevel,
  sortTable,
  handleApproveRejectDoc,
}) {
  const { t } = useTranslation('modules/partner-overview');
  const { t: tCommon } = useTranslation('common');
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [openPopoverId, setOpenPopoverId] = useState(null);

  const selectedTier = localStorage.getItem('SupplierSelectedTier') || 'Tier1';
  const isProductionLocationTier = selectedTier !== 'Tier1';

  const handleToggleAction = (index, nextShow) => {
    setOpenPopoverId(nextShow ? index : null);
  };

  const showComment = (item) => {
    const hasInfo = item?.archivedBy?.firstName || item?.archiveComment;
    return (
      <OverlayTrigger
        trigger={['hover', 'focus']}
        placement="top"
        rootClose
        overlay={
          <Popover className="custom-popover modified-popover">
            <div className="p-2">
              {hasInfo ? (
                <>
                  {item?.archivedBy?.firstName && (
                    <p className="text-left m-0">
                      {`${item?.archivedBy?.firstName} ${item.archivedBy?.lastName} | `}
                      {formatDateOrDash(item?.archivedAt, dateFormate)}
                    </p>
                  )}
                  {item?.archiveComment && <p className="m-0">{item?.archiveComment}</p>}
                </>
              ) : (
                <p className="m-0">-</p>
              )}
            </div>
          </Popover>
        }
      >
        <button type="button" className="btn">
          <CommentsIcon size={24} />
          <span className="text">{t('SUPPLIER_DOCUMENTS.COMMENT')}</span>
        </button>
      </OverlayTrigger>
    );
  };

  const fetchFromS3 = (element) => {
    return element ? encodeURI(`https://${process.env.REACT_APP_BUCKET_NAME}.s3.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${element}`) : '';
  };

  const handleDownload = async (element) => {
    if (element?.s3Key) {
      fetch(fetchFromS3(element?.s3Key))
        .then((response) => response.blob())
        .then((blob) => {
          const url = window.URL.createObjectURL(new Blob([blob]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', element?.fileName);
          document.body.appendChild(link);
          link.click();
          link.parentNode.removeChild(link);
        })
        .catch((error) => console.log('Error downloading file:', error));
    } else {
      toasterMsg(t('ADD_EDIT_COMPONENET.NO_DOCUMENT_PREVIEW'), 'danger');
    }
  };

  const handleDownloadTemplate = async (element) => {
    const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
    const objDownload = {
      documentId: element?._id,
      supplierId: element?.supplierId,
      timeZone,
    };
    setIsLoading(true);
    const res = await dispatch(supplierAction.downloadTemplate(objDownload));

    if (res?.code === 200) {
      const fileName = `${element?.title}.pdf`;
      const pdfPath = res?.data?.pdfPath;
      const fileUrl = pdfPath?.startsWith('http') ? pdfPath : `${imageUrl}/${pdfPath?.split('./static/')?.[1]}`;

      fetch(fileUrl)
        .then((response) => response.blob()) // Convert response to Blob
        .then((blob) => {
          const fileObjectURL = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = fileObjectURL;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(fileObjectURL); // Clean up
        })
        .catch((error) => console.error('Download error:', error));
    }
    setIsLoading(false);
  };

  const fetchFactoryList = async () => {
    const payload = {
      pageIndex: 1,
      pageSize,
      supplierId,
    };
    await dispatch(supplierAction.fetchSupplierComponents(payload));
  };

  useEffect(() => {
    fetchFactoryList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showCreatedandModified = (item) => {
    if (!item?.createdAt && !item?.updatedAt) return null;
    const rows = [
      item?.createdBy && { label: t('SUPPLIER_DOCUMENTS.CREATED'), value: `${item.createdBy}, ${formatDateOrDash(item?.createdAt, dateFormate)}` },
      item?.modifiedBy && { label: t('SUPPLIER_DOCUMENTS.MODIFIED'), value: `${item.modifiedBy}, ${formatDateOrDash(item?.updatedAt, dateFormate)}` },
    ].filter(Boolean);
    return (
      <BombiixInfoPopover placement="left" rows={rows}>
        <button type="button" className="btn">
          <InfoIcon size={24} />
          <span className="text">{t('SUPPLIER_DOCUMENTS.INFO')}</span>
        </button>
      </BombiixInfoPopover>
    );
  };
  const actionPanel = (item, index) => {
    const isPending = item.status === 'Pending' || item.status === 'pending';
    const canEdit = CostingAddEditAccess && isPending && !archiveSection;
    const canDelete = CostingDeleteAccess && isPending;
    const showEditDeletePanel = canEdit || canDelete;

    return (
      <div className="doc-row-actions__group">
        {showEditDeletePanel ? (
          <>
            {canEdit && (
              <CustomTooltip placement="top" tooltipText="Edit">
                <button className="bombiix-icon-container" type="button" aria-label="Edit" onClick={(e) => onEdit(e, item._id, index, item)}>
                  <EditIcon size={24} />
                </button>
              </CustomTooltip>
            )}
            {canDelete && (
              <CustomTooltip placement="top" tooltipText="Delete">
                <button className="bombiix-icon-container" type="button" aria-label="Delete" onClick={() => onDelete(item._id, item)}>
                  <DeleteIcon size={24} />
                </button>
              </CustomTooltip>
            )}
          </>
        ) : (
          <>
            {item.type !== 'Template' && (item?.s3Key || item?.document) && (
              <CustomTooltip placement="top" tooltipText={t('SUPPLIER_DOCUMENTS.PREVIEW')}>
                <button type="button" className="bombiix-icon-container" aria-label="Preview" onClick={() => onPreview(item)}>
                  <PreviewIcon size={24} />
                </button>
              </CustomTooltip>
            )}
            {item.type !== 'Template' ? (
              <CustomTooltip placement="top" tooltipText={t('SUPPLIER_DOCUMENTS.DOWNLOAD')}>
                <button type="button" className="bombiix-icon-container" aria-label="Download" onClick={() => handleDownload(item)}>
                  <DownloadIcon size={24} />
                </button>
              </CustomTooltip>
            ) : (
              <CustomTooltip placement="top" tooltipText={t('SUPPLIER_DOCUMENTS.DOWNLOAD')}>
                <button
                  type="button"
                  className="bombiix-icon-container"
                  aria-label="Download"
                  onClick={() => handleDownloadTemplate(item)}
                  disabled={isLoading}
                >
                  <DownloadIcon size={24} />
                </button>
              </CustomTooltip>
            )}
          </>
        )}
        <OverlayTrigger
          placement="left"
          trigger="click"
          rootClose
          onToggle={(nextShow) => handleToggleAction(index, nextShow)}
          overlay={
            <Popover className="table-action-popup bombiix-action-menu">
              <Popover.Content>
                <ul>
                  {/*
                      A document stored only under `document` previews fine —
                      `PreviewModal` reads `s3Key || document` — but gating on
                      `s3Key` alone hid the entry for those records, while
                      Download stayed visible because it never checked.
                    */}
                  {item.type !== 'Template' && (item?.s3Key || item?.document) && (
                    <li>
                      <button type="button" onClick={() => onPreview(item)} className="btn">
                        <PreviewIcon size={24} />
                        <span className="text">{t('SUPPLIER_DOCUMENTS.PREVIEW')}</span>
                      </button>
                    </li>
                  )}
                  {item.type !== 'Template' && (
                    <li>
                      <button type="button" onClick={() => handleDownload(item)} className="btn">
                        <DownloadIcon size={24} />
                        <span className="text">{t('SUPPLIER_DOCUMENTS.DOWNLOAD')}</span>
                      </button>
                    </li>
                  )}
                  {item.type === 'Template' && (
                    <li>
                      <button type="button" onClick={() => handleDownloadTemplate(item)} disabled={isLoading} className="btn">
                        <DownloadIcon size={24} />
                        <span className="text">{t('SUPPLIER_DOCUMENTS.DOWNLOAD')}</span>
                      </button>
                    </li>
                  )}
                  {!isFactoryLevel && CostingAddEditAccess && item.type !== 'Template' && item.status === 'Pending' && !archiveSection && (
                    <li>
                      <button type="button" onClick={(e) => onRenew(e, item, item._id)} className="btn">
                        <UpdateIcon size={24} />
                        <span className="text">{t('SUPPLIER_DOCUMENTS.RENEW')}</span>
                      </button>
                    </li>
                  )}
                  {!isFactoryLevel && CostingAddEditAccess && (
                    <li>
                      <button type="button" onClick={() => handleArchiveCard(item?._id, item)} className="btn">
                        {item?.isArchived ? <UnarchiveIcon size={24} /> : <ArchiveIcon size={24} />}
                        <span className="text">{item?.isArchived ? t('SUPPLIER_DOCUMENTS.UNARCHIVE') : t('SUPPLIER_DOCUMENTS.ARCHIVE')}</span>
                      </button>
                    </li>
                  )}
                  {!isFactoryLevel && CostingAddEditAccess && !archiveSection && (
                    <li>
                      <button type="button" onClick={() => handleDuplicateById(item)} className="btn">
                        <DuplicateIcon size={24} />
                        <span className="text">{t('SUPPLIER_DOCUMENTS.DUPLICATE')}</span>
                      </button>
                    </li>
                  )}
                  {!isFactoryLevel && CostingAddEditAccess && !archiveSection && !(item?.status === 'pending' || item?.status === 'Pending') && (
                    <li>
                      <button type="button" onClick={() => handleApproveRejectDoc(item, true, item)} className="btn">
                        <ClearIcon size={24} />
                        <span className="text">Clear</span>
                      </button>
                    </li>
                  )}
                  {item?.isArchived && <li>{showComment(item)}</li>}
                  <li>{showCreatedandModified(item)}</li>
                </ul>
              </Popover.Content>
            </Popover>
          }
        >
          <button type="button" className="bombiix-icon-container" aria-label="more actions">
            {openPopoverId === index ? <CloseIcon size={24} /> : <MoreIcon size={24} />}
          </button>
        </OverlayTrigger>
      </div>
    );
  };
  const dateFormat = 'DD-MMMM-YYYY';

  const approveRejWasArr = (ele) => {
    return !ele?.isExpanded && ele?.approveRejectedBy?.length === 1
      ? `${ele?.approveRejectedBy?.[0]?.firstName} ${ele?.approveRejectedBy?.[0]?.lastName} | `
      : `${ele?.approveRejectedBy?.firstName} ${ele?.approveRejectedBy?.lastName} | `;
  };

  const forCurrentStatus = (ele) => {
    return Array.isArray(ele?.status)
      ? !ele?.status?.[0] || ele?.status?.[0] === 'Pending' || ele?.status?.[0] === 'pending' || ele?.status?.[0] === null
      : !ele?.status || ele?.status === 'Pending' || ele?.status === 'pending' || ele?.status === null;
  };

  const ifoneAppRej = (ele) => {
    return !ele?.approveRejectedBy || (Array.isArray(ele.approveRejectedBy) && isEmpty(ele.approveRejectedBy)) ? '' : approveRejWasArr(ele);
  };

  const forAppRej = (ele) => {
    return !ele?.isExpanded && ele?.approveRejectedBy?.length > 1 ? '' : ifoneAppRej(ele);
  };
  const selectedDataArr = (ele) => {
    return !ele?.isExpanded && ele?.selectedDate?.length === 1 ? moment(ele?.selectedDate[0]).format(dateFormat) : moment(ele?.selectedDate).format(dateFormat);
  };
  const ifoneAppAt = (ele) => {
    return !ele?.selectedDate || !ele?.selectedDate[0] ? '' : selectedDataArr(ele);
  };

  const forCmntApprovedAt = (ele) => {
    return !ele?.isExpanded && ele?.expandData?.length > 1 ? '' : ifoneAppAt(ele);
  };
  const cmntParts = (ele) => {
    if (ele?.approveBy) {
      return ele?.statusComment?.length === 1 && forCurrentStatus(ele) ? null : (
        <button aria-label="info" type="button" className="bombiix-icon-container">
          <OverlayTrigger
            trigger={['hover', 'focus']}
            rootClose
            overlay={
              <Popover className="custom-popover modified-popover">
                <div className="p-2">
                  <p className="mb-1">
                    {Array.isArray(ele?.approveBy) && forAppRej(ele)}
                    {!Array.isArray(ele?.approveBy) && ele?.approveBy && `${ele?.approveBy} | `}
                    {Array.isArray(ele?.statusDate) && forCmntApprovedAt(ele)}
                    {!Array.isArray(ele?.statusDate) && formatDateOrDash(ele?.statusDate, dateFormat)}
                  </p>
                  {!isEmpty(ele?.statusComment) && <p className="mb-0 comment-box">{ele?.statusComment}</p>}
                </div>
              </Popover>
            }
          >
            <InfoIcon size={24} />
          </OverlayTrigger>
        </button>
      );
    }
    return null;
  };

  function getFactoryNames(code) {
    if (code?.length > 0) {
      return code.join(', ');
    }
    return '';
  }

  const sortHeaderParams = {
    activeSortField: sortTable?.sortPropertyName,
    activeSortOrder: sortTable?.sortOrder,
    onSort: onSortColumn,
  };

  /** Builds a server-sorted column; AG Grid never reorders, the caller refetches. */
  const sortedColumn = (field, headerName, width) => ({
    field,
    headerName,
    width,
    sortable: false,
    headerComponent: ServerSortHeader,
    headerComponentParams: { ...sortHeaderParams, sortField: field },
  });

  const columnDefs = [];

  if (!isFactoryLevel) {
    columnDefs.push({
      colId: 'select',
      headerName: '',
      width: 56,
      minWidth: 56,
      maxWidth: 56,
      sortable: false,
      resizable: false,
      suppressHeaderMenuButton: true,
      headerComponent: DocumentSelectAllHeader,
      headerComponentParams: { checked: allSelectedDocuments, label: tCommon('COMMON_LABEL.SELECT_ALL'), onToggle: handleSelectAll },
      cellRenderer: DocumentSelectRowCell,
      cellRendererParams: { selectedDocuments: Documents, label: tCommon('BTN_FILTER.SELECT'), onToggle: selectDocumentCheckBox },
    });
  }

  columnDefs.push({
    colId: 'documentType',
    headerName: t('SUPPLIER_DOCUMENT.DOCUMENT_TYPE'),
    width: 160,
    sortable: false,
    cellRenderer: ({ data }) => (data?.documentType?.isDeleted ? '' : data?.documentType?.name) || '-',
  });

  columnDefs.push({
    ...sortedColumn('title', t('SUPPLIER_DOCUMENT.DOCUMENT_NAME'), 200),
    cellRenderer: DocumentTitleCell,
  });

  if (!isFactoryLevel) {
    columnDefs.push({
      colId: 'factories',
      headerName: isProductionLocationTier ? t('SUPPLIER_DOCUMENT.PRODUCTION_LOCATIONS') : t('SUPPLIER_DOCUMENT.FACTORIES'),
      width: 160,
      sortable: false,
      cellRenderer: ({ data }) => getFactoryNames(data?.factoryCode) || '-',
    });
    columnDefs.push({
      ...sortedColumn('templateName', t('SUPPLIER_DOCUMENT.TEMPLATE_NAME'), 160),
      cellRenderer: ({ data }) => (data?.type === 'Template' && data?.templateName ? shortenandHoverString(data.templateName, 20) : ''),
    });
  }

  columnDefs.push({
    ...sortedColumn('expiredAt', t('SUPPLIER_DOCUMENT.EXPIRY_DATE'), 160),
    cellRenderer: DocumentExpiryCell,
  });

  columnDefs.push({
    colId: 'statusDate',
    headerName: t('SUPPLIER_DOCUMENT.STATUS_DATE'),
    width: 160,
    sortable: false,
    cellRenderer: ({ data }) => (Array.isArray(data?.statusDate) ? forCmntApprovedAt(data) : formatDateOrDash(data?.statusDate, dateFormat)),
  });

  columnDefs.push({
    colId: 'statusComments',
    headerName: t('SUPPLIER_DOCUMENT.STATUS_COMMENTS'),
    width: 240,
    flex: 1,
    minWidth: 180,
    sortable: false,
    cellRenderer: ({ data }) => (Array.isArray(data?.statusComment) ? data.statusComment.filter(Boolean).join(', ') : data?.statusComment || ''),
  });

  columnDefs.push({
    colId: 'status',
    headerName: t('SUPPLIER_DOCUMENT.STATUS'),
    width: 200,
    sortable: false,
    cellRenderer: DocumentStatusCell,
    cellRendererParams: { renderComment: archiveSection ? null : cmntParts, leadingLabel: t('SUPPLIER_DOCUMENT.UPLOADED') },
  });

  return (
    <div className="supplier-doc-grid">
      <BombiixAGGrid
        rowData={loading ? [] : supportingDocument ?? []}
        columnDefs={columnDefs}
        getRowId={(row) => row?._id}
        readOnly
        sortable={false}
        loading={loading}
        /*
         * 3 actions at 24px on the shared 8px pitch = 88px, plus a 10px inset
         * either side. 150 left a 34px gap on the right against 11px on the left.
         */
        rowActionsWidth={108}
        rowActionsClassName="doc-row-actions"
        renderRowActionExtras={({ data, node }) => actionPanel(data, node?.rowIndex)}
      />
    </div>
  );
}

export default DocumentTabel;
